import React, { useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import styles from './reviewCardStyles.module.scss';
import FitSlider from '../FitSlider';
import {
  Helpfulness,
  SecondaryInfo,
  ProductReview,
  FitInfo,
  ReviewImage,
  ProductReviewFeedbacks,
  ProductReviewVote,
} from '../../../types/reviews';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { StarsUI } from '../../Reviews';
import {
  destructureSecondaryInfo,
  getProductReviewFeedbackKeyName,
  generateVotingFeedbackCookieValue,
  postReviewVoting,
  reportReview,
  updateReviewFeedback,
} from 'src/scripts/utils/reviews';

const ReviewCard: React.FC<{
  data: ProductReview;
  feedbacks: ProductReviewFeedbacks;
}> = ({ data, feedbacks }) => {
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT);

  const { images, title, text, author, fit, helpfulness, secondary, rating } = data;
  // Destructure secondary info
  const { sizeInfo, otherInfo } = destructureSecondaryInfo(secondary);
  const reviewAuthor = author || 'Anonymous';
  return (
    <div className={styles.review}>
      {!isMobile ? (
        <div className={styles.reviewColLeft}>
          <p className={`body ${styles.labelText} ${styles.name}`}>{reviewAuthor}</p>
          <InformationLeft secondary={otherInfo} />
        </div>
      ) : null}
      <div className={styles.reviewColMiddle}>
        {isMobile ? <h5 className={`h5 notranslate ${styles.title}`}>{title}</h5> : null}
        <div className={styles.rating}>
          <StarsUI score={rating} starSize="20px" color="var(--highlight-medium)" />
        </div>
        {isMobile ? <p className={`body notranslate ${styles.labelText} ${styles.name}`}>{reviewAuthor}</p> : null}
        {!isMobile ? <h5 className={`h5 notranslate ${styles.title}`}>{title}</h5> : null}
        <p className={`body notranslate ${styles.description}`}>{text}</p>

        {isMobile ? (
          <>
            <InformationLeft secondary={otherInfo} />
            <InformationRight secondary={sizeInfo} />
            {fit ? <ReviewCardFitSlider fit={fit} /> : null}
          </>
        ) : null}

        {images ? <ReviewImages images={images} /> : null}
        {helpfulness ? <ReviewCardFooter helpfulness={helpfulness} feedbacks={feedbacks} /> : null}
      </div>
      {!isMobile ? (
        <div className={styles.reviewColRight}>
          <InformationRight secondary={sizeInfo} />
          {fit ? <ReviewCardFitSlider fit={fit} /> : null}
        </div>
      ) : null}
    </div>
  );
};

const ReviewCardFitSlider = ({ fit }: { fit: FitInfo }) => {
  const { rating, range } = fit;

  return <FitSlider averageRating={rating} fitRange={range} />;
};

const InformationLeft = ({ secondary }: { secondary: SecondaryInfo[] }) => {
  if (secondary.length === 0) return null;
  return (
    <div className={styles.information}>
      <table>
        {secondary.map((item, index) => (
          <tr key={index}>
            <td>
              <span className={`body notranslate  ${styles.labelText}`}>{item.label}:</span>
            </td>
            <td>
              <span className={`body notranslate ${styles.labelText} semibold`}>{item.value}</span>
            </td>
          </tr>
        ))}
      </table>
    </div>
  );
};

const InformationRight = ({ secondary }: { secondary: SecondaryInfo[] }) => {
  if (secondary.length === 0) return null;
  return (
    <div className={styles.information}>
      <ul>
        {secondary.map((item, index) => (
          <li key={index}>
            <span className={`body notranslate ${styles.labelText}`}> {item.label}:</span>
            <span className={`body notranslate ${styles.labelText} semibold`}> {item.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReviewCardFooter = ({
  helpfulness,
  feedbacks,
}: {
  helpfulness: Helpfulness;
  feedbacks: ProductReviewFeedbacks;
}) => {
  const { negative, positive = 0, content_id } = helpfulness;

  const votingFeedbackValue = feedbacks[getProductReviewFeedbackKeyName(content_id)];
  const reportFeedbackValue = feedbacks[getProductReviewFeedbackKeyName(content_id, true)];

  const [votingFeedback, setVotingFeedback] = useState(
    typeof votingFeedbackValue === 'object' ? votingFeedbackValue : undefined
  );
  const [alreadyReported, setReportFeedback] = useState(
    typeof reportFeedbackValue === 'boolean' ? reportFeedbackValue : false
  );

  const isPositiveVoted = votingFeedback?.v === 'p';
  const isNegativeVoted = votingFeedback?.v === 'n';
  const votedReviewCount = votingFeedback?.c || 0;
  const isVoted = isPositiveVoted || isNegativeVoted;

  const positiveCount = votedReviewCount >= positive && isPositiveVoted ? votedReviewCount : positive;
  const negativeCount = votedReviewCount >= negative && isNegativeVoted ? votedReviewCount : negative;

  const triggerVoting = async (content_id: number, voteType: ProductReviewVote, count: number) => {
    if (isVoted) return;
    const { success } = await postReviewVoting(content_id, voteType);
    if (success) {
      updateReviewFeedback({ content_id, feedbackType: voteType, count });
      setVotingFeedback(generateVotingFeedbackCookieValue(voteType, count));
    }
  };

  const handleReport = async () => {
    if (alreadyReported) return;
    const { success } = await reportReview(content_id);
    if (success) {
      updateReviewFeedback({ content_id, feedbackType: 'report' });
      setReportFeedback(true);
    }
  };

  return (
    <div className={styles.reviewCardFooter}>
      <span className={`${styles.helpful} ${isVoted ? styles.voted : ''}`}>
        <span className={`body notranslate ${styles.labelText} ${styles.helpfulText}`}>Helpful?</span>
        <button
          aria-label="Helpful"
          className={styles.helpfulLiked}
          onClick={() => triggerVoting(content_id, ProductReviewVote.Positive, positive)}
        >
          {isPositiveVoted ? (
            <span className="alo-thumb-up-solid-icon"></span>
          ) : (
            <span className="alo-thumb-up-icon"></span>
          )}
          <span className={styles.helpfulCount}>{positiveCount}</span>
        </button>
        <button
          aria-label="Not Helpful"
          className={styles.helpfulDisLiked}
          onClick={() => triggerVoting(content_id, ProductReviewVote.Negative, negative)}
        >
          {isNegativeVoted ? (
            <span className="alo-thumb-down-solid-icon"></span>
          ) : (
            <span className="alo-thumb-down-icon"></span>
          )}
          <span className={styles.helpfulCount}>{negativeCount}</span>
        </button>
      </span>
      <button
        className={`body notranslate ${styles.labelText} ${styles.reportText}`}
        onClick={handleReport}
        disabled={alreadyReported}
      >
        {alreadyReported ? 'Reported' : 'Report'}
      </button>
    </div>
  );
};

const ReviewImages = ({ images }: { images: ReviewImage[] }) => {
  if (!images || images.length === 0) return null;
  return (
    <div className={styles.reviewImages}>
      <div className={styles.reviewImagesScroller}>
        <ul>
          {images.map((image) => (
            <li>
              <img src={image.normal} alt="Review Images" />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ReviewCard;
